import dayjs from 'dayjs';
import type { Hours, WorkloadStatus } from './tasks.types';

export const num = (h: Hours | null | undefined): number => (h === null || h === undefined ? 0 : Number(h));

/** "7.5h" — hours as the screens show them. */
export function fmtHours(h: Hours | null | undefined): string {
  if (h === null || h === undefined) return '—';
  const n = Number(h);
  return `${Number.isInteger(n) ? n : n.toFixed(2).replace(/0$/, '')}h`;
}

export function fmtPct(h: Hours | null | undefined): string {
  if (h === null || h === undefined) return '—';
  return `${Number(h).toFixed(1).replace(/\.0$/, '')}%`;
}

export const fmtDate = (d: string | null | undefined) => (d ? dayjs(d).format('DD MMM YYYY') : '—');
export const fmtDateTime = (d: string | null | undefined) => (d ? dayjs(d).format('DD MMM YYYY, HH:mm') : '—');
export const isoDate = (d: dayjs.Dayjs | Date | string) => dayjs(d).format('YYYY-MM-DD');
export const today = () => isoDate(new Date());

/** Monday of the ISO week containing `d`. */
export const weekStart = (d: string) => {
  const day = dayjs(d);
  const offset = (day.day() + 6) % 7;
  return isoDate(day.subtract(offset, 'day'));
};

export const WORKLOAD_COLORS: Record<WorkloadStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  UNDERUTILIZED: 'info',
  NORMAL: 'success',
  HIGH: 'warning',
  OVERLOADED: 'error',
  NO_CAPACITY: 'default',
};

/**
 * What a task's drawings are called, after its type of work activity: "Design drawings" for
 * Design, plain "Drawings" for a type already named Drawing, "Structural drawings" before a
 * type is chosen.
 */
export const drawingsLabel = (typeName: string | null | undefined) => {
  const name = (typeName ?? '').trim();
  if (!name) return 'Structural drawings';
  if (/drawings?$/i.test(name)) return /s$/i.test(name) ? name : `${name}s`;
  return `${name} drawings`;
};

export const humanize = (code: string | null | undefined) =>
  code ? code.toLowerCase().replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '';

/** The message the API interceptor attached, or a fallback. */
export const errorMessage = (e: unknown, fallback = 'Something went wrong') =>
  (e as { userMessage?: string })?.userMessage || (e as Error)?.message || fallback;

/** True when the server refused a plan because it would put someone over capacity. */
export const isOverCapacity = (e: unknown) => /over capacity/i.test(errorMessage(e, ''));
